<template>
    <teleport to="#modal">
        <Modal v-model:visible="visible" backdrop="true">
            <template #header>
                <p class="font-bold text-xl">Importa previsionale commerciale</p>
            </template>
            <template #main>
                <div class="flex items-end gap-3">
                    <div class="flex flex-col justify-center items-center mb-3 grow">
                        <Field field_name="Anno" v-model="anno" type="number" :inputAttrs="{step:'1'}"></Field>
                    </div>
                    <div class="flex justify-center mb-3">
                        <FileUpload
                            v-model:uploaded_files="uploaded"
                            accept=".xlx,.xls,.xlsx"
                        >
                            <template v-slot:default="{props, methods}">
                                <div class="flex flex-col justify-center items-center">
                                    <NextButton variant="outline-secondary" @click="methods.open"
                                                class="w-56 h-10 px-4 grow">
                                        <i class="icon icon-graffetta"></i> Allega
                                    </NextButton>
                                </div>
                            </template>
                        </FileUpload>
                    </div>
                </div>
                <div v-if="uploaded" class="grid grid-cols-1 gap-3">
                    <a class="underline text-sm text-next-atp pt-3 cursor-pointer"
                       @click="downloadFile(f?.id)"
                       target="_blank"
                       v-for="(f,idx) in uploaded" :key="idx">
                        <span>{{ f.name }}</span>
                    </a>
                </div>

                <div class="flex flex-col col-span-2 justify-center items-center pt-3">
                    <Radio value="aggiorna" name="type" v-model="tipologia_scrittura" checkClass="w-3 h-3">Aggiorna
                        valori esistenti
                    </Radio>
                    <Radio value="sovrascrivi" name="type" v-model="tipologia_scrittura" checkClass="w-3 h-3">Cancella
                        esistenti
                    </Radio>
                </div>

                <template v-if="errors.length">
                    <Alert class="mt-3 bg-next-orange">Importazione interrotta a causa dei seguenti errori</Alert>

                    <p v-for="e in errors" class="text-next-orange">{{ e }}</p>
                </template>

            </template>
            <template #footer>
                <div class="flex justify-end">
                    <div class="flex justify-end pt-3">
                        <NextButton variant="outline-secondary"
                                    @click="visible = false"
                                    class="col-start-3 py-2 px-4">
                            Chiudi
                        </NextButton>
                        <NextButton variant="primary"
                                    @click="importa"
                                    class="ml-4 py-2 px-4">
                            Importa
                        </NextButton>
                    </div>
                </div>
            </template>
        </Modal>
    </teleport>
    <div class="m-auto px-8 md:px-20 pb-8">
        <div class="flex justify-between items-center">

            <h1 class="py-5 text-2xl font-bold">Previsionale commerciale per {{ business_unit?.nome }}</h1>

            <NextButton @click="visible = !visible" class="py-1 px-3">
                Importa
            </NextButton>

            <NextButton component="router-link" :to="{name:'DettaglioBilancioPrevisionaleNew', params:{id:id}}"
                        class="py-1 px-3">
                Nuovo
            </NextButton>
        </div>

        <div class="flex justify-between">
            <div class="flex flex-col mb-3">
                <Field field_name="Cerca" @update:modelValue="lista.debounceSearch" v-model="lista.search"
                       placeholder="Cerca"></Field>
            </div>
            <Paginator class="" :pagination="lista.paginate"></Paginator>
        </div>
        <div class="rotate-x-180 overflow-x-scroll">
            <NextTable class="rotate-x-180 table-auto w-full border-separate border-spacing-3 py-4"
                       v-model:cols="cols"
                       :rows="lista.rows" @order-update="lista.updateCols">

                <template v-for="(c, j) in lista.rows" :key="j" v-slot:[`td_${j}_3`]="{context}">
                    {{ $filters.toEuroCurrency(context.row.previsto) }}
                </template>

                <template v-for="(c, j) in lista.rows" :key="j" v-slot:[`td_${j}_4`]="{context}">
                    {{ $filters.toEuroCurrency(context.row.progresso) }}
                </template>

                <template v-for="(c, j) in lista.rows" :key="j" v-slot:[`td_${j}_5`]="context">
                    <router-link
                        :to="{name:'DettaglioBilancioPrevisionale', params:{id:id, bid:context.context.row.id}}"
                        class="text-center inset-0 absolute flex items-center justify-center"><i
                        class="icon-chevron_destra"></i></router-link>
                </template>
            </NextTable>
        </div>
        <InfiniteLoading @infinite="lista.fetchNextPage"
                         :identifier="lista.infiniteIdentifier" style="min-height: 1px">
            <template #complete>
                <div class="text-center pb-3">Non ci sono altre righe.</div>
            </template>
        </InfiniteLoading>

    </div>
</template>

<script lang="ts">
import {Options, setup, Vue} from 'vue-class-component';
import Loading from "@/components/Loading.vue";
import NextButton from "@/components/NextButton.vue";
import Field from "@/components/Field.vue";
import businessUnitsManager from "@/api/BusinessUnits";
import Alert from "@/components/Alert.vue";
import Lista from "@/setup/ListaCerca";
import NextTable from "@/components/NextTable.vue";
import Paginator from "@/components/Paginator.vue";
import InfiniteLoading from "v3-infinite-loading";
import Modal from "@/components/Modal.vue";
import FileUpload from "@/components/FileUpload.vue";
import Checkbox from "@/components/Checkbox.vue";
import Radio from "@/components/Radio.vue";

@Options({
    name: 'BilanciPrevisionaliBu',
    inject: ['downloadFile'],
    components: {
        Radio,
        Checkbox,
        FileUpload,
        Modal,
        Paginator, NextTable, InfiniteLoading,
        Alert,
        Field,
        NextButton,
        Loading
    },
    props: {
        id: {
            type: [String, Number],
            required: true
        }
    }
})
export default class BilanciPrevisionaliBu extends Vue {
    lista: any = setup(() => Lista(businessUnitsManager, this.cols, businessUnitsManager.bilanciPrevisionali));

    visible: boolean = false
    tipologia_scrittura: string = 'aggiorna'
    anno: number = 0
    id!: string | number
    can_edit = true
    business_unit = {}

    uploaded:any[] = []
    errors = []

    created() {
        this.anno = new Date().getFullYear()
        businessUnitsManager.businessUnit(this.id).then((res: any) => {
            this.business_unit = res.data
        })
        this.lista.callBinds = [this.id]
        this.lista.fetchData()
    }

    importa() {
        businessUnitsManager.importaBilanciPrevisionali(this.id, {
            file_id: this.uploaded[0].id,
            anno: this.anno,
            type: this.tipologia_scrittura
        }).then((res: any) => {
            this.errors = []
            this.uploaded = []
            this.visible = false
        }).catch((res) => {
            this.errors = res.response.data.errors
        })
    }

    cols = [
        {
            name: 'Cliente',
            prop: 'cliente_denominazione',
            orderable: true,
            ordProp: 'anagrafiche_denominazioni.denominazione',
            class: 'text-left whitespace-nowrap',
            td_class: 'px-2 bg-next-gray-md-dark',
            resizable: false
        },
        {
            name: 'Business Unit',
            prop: 'business_unit_nome',
            orderable: true,
            ordProp: 'business_units.nome',
            class: 'text-left whitespace-nowrap',
            td_class: 'px-2 bg-next-gray-md-dark',
            resizable: false
        },
        {
            name: 'Anno',
            prop: 'anno',
            orderable: true,
            class: 'text-left whitespace-nowrap',
            td_class: 'px-2 bg-next-gray-md-dark',
            resizable: false
        },
        {
            name: 'Fatt. previsionale',
            prop: 'previsto',
            orderable: true,
            class: 'text-left whitespace-nowrap',
            td_class: 'text-right px-2 bg-next-gray-md-dark',
            resizable: false
        },
        {
            name: 'Fatt. Progressivo',
            prop: 'progresso',
            orderable: true,
            class: 'text-left whitespace-nowrap',
            td_class: 'text-right px-2 bg-next-gray-md-dark',
            resizable: false
        },
        {
            name: '',
            prop: '',
            orderable: false,
            class: 'min-w-10 w-10 whitespace-nowrap',
            td_class: 'relative bg-next-gray-md-dark hover:bg-next-orange hover:text-white',
            resizable: false
        }
    ]
}

</script>
